import { ApiError, type QrResponseDto } from "@/api";
import { useGeneratePromptPayQr } from "@/hooks/api/useGeneratePromptPayQr";
import { useValidateQrPayment } from "@/hooks/api/useValidateQrPayment";
import { useFrameStore } from "@/lib/store/frameStore";
import { usePaymentStore } from "@/lib/store/paymentStore";
import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { track, trackError } from "@/lib/analytics/track";
import { PRICING } from "@/lib/constants/pricing";

export function useQrPayment() {
  const navigate = useNavigate();
  const { setPaymentPayload, setQr, qr, resetQr, isGeneratingQr, setIsGeneratingQr } = usePaymentStore();
  const { frame } = useFrameStore();

  const [error, setError] = useState<string | null>(null);

  const { mutate: generateQr } = useGeneratePromptPayQr();
  const { mutate: validateQrPayment, isPending: isPaymentVerifying } = useValidateQrPayment();

  const handleGenerateQr = ({
    onSuccess,
    onError,
  }: {
    onSuccess?: (data: QrResponseDto) => void;
    onError?: (error: Error) => void;
  }) => {
    if (!frame) return;

    if (qr) {
      const expiry = new Date(qr.expiry);
      const isExpired = expiry.getTime() <= new Date().getTime();
      const frameChanged = qr.childFrameId !== frame.childFrameId;

      if (!isExpired && !frameChanged) {
        track("payment_qr_reused", {
          transaction_id: qr.transactionId,
          child_frame_id: qr.childFrameId,
        });
        onSuccess?.(qr);
        return;
      } else {
        resetQr();
      }
    }
    
    setError(null);
    setIsGeneratingQr(true);
    track("payment_qr_generate_start", { child_frame_id_present: Boolean(frame.childFrameId) });
    const childFrameId = frame.childFrameId;

    generateQr(
      // beam used 100 baht as the unit
      { childFrameId, amount: PRICING.DEFAULT_AMOUNT * 100 },
      {
        onSuccess: (data) => {
          setQr(data);
          track("payment_qr_generate_success", { transaction_id_present: Boolean(data?.transactionId) });
          onSuccess?.(data);
        },
        onError: (error) => {
          setIsGeneratingQr(false);
          console.error(error);
          if (error instanceof ApiError) {
            const message = error.body?.error?.userMessage || error.body?.error?.message || "Failed to generate QR code";
            setError(message);
            track("payment_qr_generate_error", { message });
            trackError({
              type: "payment",
              message: message,
              source: "manual",
              stack: error instanceof Error ? error.stack : undefined,
            });
            toast.error(message);
          } else {
            const message = "Failed to generate QR code";
            setError(message);
            track("payment_qr_generate_error", { message });
            trackError({
              type: "payment",
              message: message,
              source: "manual",
              stack: error instanceof Error ? error.stack : undefined,
            });
            toast.error(message);
            onError?.(error);
          }
        },
      }
    );
  };

  const handlePaymentVerification = () => {
    if (!qr?.transactionId) {
      toast.error("QR code is not generated. Please generate a QR code first.");
      return;
    }

    if (!qr.childFrameId) {
      toast.error("Frame information is missing. Please select a frame again.");
      return;
    }

    track("payment_verify_start", {
      transaction_id_present: Boolean(qr.transactionId),
    });
    validateQrPayment(
      {
        transactionId: qr.transactionId,
      },
      {
        onSuccess: (data) => {
          setPaymentPayload({
            token: data.token,
            transactionId: qr.transactionId,
            childFrameId: data.childFrameId, // Use backend childFrameId
          });
          track("payment_verify_success", {
            token_present: Boolean(data?.token),
            child_frame_id: data.childFrameId,
          });
          navigate("/camera");
          resetQr();
        },
        onError: (error) => {
          console.error(error);
          if (error instanceof ApiError) {
            const message = error.body?.error?.userMessage || error.body?.error?.message || "Failed to validate QR payment";
            track("payment_verify_error", { message });
            trackError({
              type: "payment",
              message: message,
              source: "manual",
              stack: error instanceof Error ? error.stack : undefined,
            });
            toast.error(message);
          } else {
            const message = "Failed to validate QR payment";
            track("payment_verify_error", { message });
            trackError({
              type: "payment",
              message: message,
              source: "manual",
              stack: error instanceof Error ? error.stack : undefined,
            });
            toast.error(message);
            console.error(error);
          }
        },
      }
    );
  };

  return {
    qr,
    error,
    isGeneratingQr,
    isPaymentVerifying,
    handleGenerateQr,
    handlePaymentVerification,
  };
}
